/**
 * SelectField Component - Dropdown select with search and multi-select support
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { ChevronDown, X, Search, Check } from 'lucide-react';
import { SelectFieldProps, SelectOption } from '../../../types/forms';
import { BaseField } from './BaseField';

export const SelectField: React.FC<SelectFieldProps> = ({
  name,
  label,
  placeholder = 'Select...',
  helpText,
  required = false,
  disabled = false,
  readonly = false,
  error,
  className = '',
  value,
  options,
  multiple = false,
  searchable = false,
  clearable = false,
  loading = false,
  onChange,
  onBlur,
  onFocus,
  onSearch,
}) => {
  const fieldId = `field-${name}`;
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = useCallback(() => {
    if (!disabled && !readonly) {
      setIsOpen(!isOpen);
      if (!isOpen && searchable) {
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    }
  }, [isOpen, disabled, readonly, searchable]);

  const handleSelect = useCallback(
    (option: SelectOption) => {
      if (multiple) {
        const currentValues = Array.isArray(value) ? value : [];
        const newValues = currentValues.includes(option.value)
          ? currentValues.filter((v) => v !== option.value)
          : [...currentValues, option.value];
        onChange?.(newValues);
      } else {
        onChange?.(option.value);
        setIsOpen(false);
        setSearchQuery('');
      }
    },
    [multiple, value, onChange]
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange?.(multiple ? [] : '');
    },
    [multiple, onChange]
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const query = e.target.value;
      setSearchQuery(query);
      onSearch?.(query);
    },
    [onSearch]
  );

  // Filter options based on search
  const filteredOptions = searchQuery
    ? options.filter((opt) =>
        opt.label.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : options;

  // Group options if groups are defined
  const groupedOptions = filteredOptions.reduce((acc, option) => {
    const group = option.group || 'default';
    if (!acc[group]) acc[group] = [];
    acc[group].push(option);
    return acc;
  }, {} as Record<string, SelectOption[]>);

  // Get display value
  const getDisplayValue = () => {
    if (multiple && Array.isArray(value)) {
      if (value.length === 0) return placeholder;
      const selectedLabels = value
        .map((v) => options.find((opt) => opt.value === v)?.label)
        .filter(Boolean);
      return selectedLabels.join(', ');
    } else {
      const selected = options.find((opt) => opt.value === value);
      return selected ? selected.label : placeholder;
    }
  };

  const isSelected = (optionValue: string) => {
    if (multiple && Array.isArray(value)) {
      return value.includes(optionValue);
    }
    return value === optionValue;
  };

  const selectClasses = `
    w-full px-3 py-2
    border rounded-md shadow-sm
    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
    ${error ? 'border-red-300' : 'border-gray-300'}
    ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white cursor-pointer'}
    ${readonly ? 'bg-gray-50' : ''}
    transition-colors duration-200
    flex items-center justify-between
  `;

  return (
    <BaseField
      name={name}
      label={label}
      helpText={helpText}
      required={required}
      disabled={disabled}
      readonly={readonly}
      error={error}
      className={className}
      fieldId={fieldId}
    >
      <div ref={dropdownRef} className="relative">
        {/* Select trigger */}
        <div
          id={fieldId}
          className={selectClasses}
          onClick={handleToggle}
          onBlur={onBlur}
          onFocus={onFocus}
          tabIndex={0}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-invalid={!!error}
        >
          <span className={`flex-1 truncate ${!value ? 'text-gray-400' : ''}`}>
            {getDisplayValue()}
          </span>
          <div className="flex items-center gap-1">
            {clearable && (value || (Array.isArray(value) && value.length > 0)) && (
              <button
                type="button"
                onClick={handleClear}
                className="p-1 hover:bg-gray-100 rounded"
                aria-label="Clear selection"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}
            <ChevronDown
              className={`w-5 h-5 text-gray-400 transition-transform ${
                isOpen ? 'rotate-180' : ''
              }`}
            />
          </div>
        </div>

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
            {/* Search input */}
            {searchable && (
              <div className="p-2 border-b border-gray-200">
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 w-4 h-4 text-gray-400" />
                  <input
                    ref={inputRef}
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder="Search..."
                    className="w-full pl-8 pr-2 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>
            )}

            {/* Loading state */}
            {loading && (
              <div className="p-4 text-center text-gray-500">Loading...</div>
            )}

            {/* Options */}
            {!loading && filteredOptions.length === 0 && (
              <div className="p-4 text-center text-gray-500">No options found</div>
            )}

            {!loading && filteredOptions.length > 0 && (
              <ul role="listbox" className="py-1">
                {Object.entries(groupedOptions).map(([group, groupOptions]) => (
                  <React.Fragment key={group}>
                    {group !== 'default' && (
                      <li className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
                        {group}
                      </li>
                    )}
                    {groupOptions.map((option) => (
                      <li
                        key={option.value}
                        role="option"
                        aria-selected={isSelected(option.value)}
                        className={`
                          px-3 py-2 cursor-pointer flex items-center justify-between
                          ${
                            isSelected(option.value)
                              ? 'bg-blue-50 text-blue-600'
                              : 'hover:bg-gray-100'
                          }
                          ${option.disabled ? 'opacity-50 cursor-not-allowed' : ''}
                        `}
                        onClick={() => !option.disabled && handleSelect(option)}
                      >
                        <span>{option.label}</span>
                        {isSelected(option.value) && (
                          <Check className="w-4 h-4 text-blue-600" />
                        )}
                      </li>
                    ))}
                  </React.Fragment>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </BaseField>
  );
};

export default SelectField;